import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Edit, Trash2, Sparkles, Users, Calendar, DollarSign } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminEarlyBird = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingOffer, setEditingOffer] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/aktionen`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOffers(response.data);
    } catch (error) {
      console.error('Error fetching offers:', error);
      toast.error('Fehler beim Laden der Angebote');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingOffer({
      name: 'OBD Live Early-Bird',
      description: 'Erstes Jahr 30% Rabatt + Dongle kostenlos',
      discount_type: 'percentage',
      discount_value: 30,
      free_item: 'obd_dongle',
      free_item_value: 49.90,
      max_customers: 100,
      valid_from: new Date().toISOString().split('T')[0],
      valid_until: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      coupon_code: 'EARLYBIRD2024',
      eligible_plan_ids: [],
      first_month_free: false,
      requires_12_month: false,
      show_as_popup: false,
      popup_page_type: 'diagnose',
      popup_delay: 10,
      is_active: true
    });
    setShowCreateForm(true);
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Convert date strings to ISO format with time
      const offerData = {
        ...editingOffer,
        valid_from: editingOffer.valid_from ? new Date(editingOffer.valid_from).toISOString() : undefined,
        valid_until: editingOffer.valid_until ? new Date(editingOffer.valid_until).toISOString() : undefined
      };
      
      if (editingOffer.id) {
        await axios.put(`${API_URL}/api/admin/aktionen/${editingOffer.id}`, offerData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Angebot aktualisiert');
      } else {
        await axios.post(`${API_URL}/api/admin/aktionen`, offerData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Angebot erstellt');
      }
      
      fetchOffers();
      setShowCreateForm(false);
      setEditingOffer(null);
    } catch (error) {
      console.error('Error saving offer:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Fehler beim Speichern');
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Wirklich löschen?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/aktionen/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Angebot gelöscht');
      fetchOffers();
    } catch (error) {
      console.error('Error deleting offer:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  if (loading) {
    return <div className="p-6">Lädt...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3" style={{ color: '#E6EDF3' }}>
            <Sparkles className="w-8 h-8" style={{ color: '#7CD4FD' }} />
            Aktionen
          </h1>
          <p className="text-gray-400 mt-2">
            Verwalten Sie zeitlich begrenzte Rabatt-Aktionen und Promotions
          </p>
        </div>
        <Button onClick={handleCreate} className="bg-gradient-to-r from-[#7CD4FD] to-[#22C55E]">
          <Plus className="w-4 h-4 mr-2" />
          Neues Angebot
        </Button>
      </div>

      {/* Offer List */}
      <div className="grid gap-6">
        {offers.map(offer => (
          <Card key={offer.id} style={{ backgroundColor: '#1A1F2E', borderColor: '#2A3142' }}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-xl font-bold" style={{ color: '#7CD4FD' }}>
                      {offer.name}
                    </h3>
                    {offer.is_active ? (
                      <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: '#22C55E20', color: '#22C55E' }}>
                        AKTIV
                      </span>
                    ) : (
                      <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: '#EF444420', color: '#EF4444' }}>
                        INAKTIV
                      </span>
                    )}
                  </div>
                  
                  <p className="text-gray-400 mb-4">{offer.description}</p>

                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                        <DollarSign className="w-4 h-4" />
                        Rabatt
                      </div>
                      <div className="text-white font-bold">
                        {offer.discount_type === 'percentage' ? `${offer.discount_value}%` : `€${offer.discount_value}`}
                      </div>
                    </div>

                    {offer.free_item && (
                      <div>
                        <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                          <Sparkles className="w-4 h-4" />
                          Gratis
                        </div>
                        <div className="text-white font-bold">
                          {offer.free_item} (€{offer.free_item_value})
                        </div>
                      </div>
                    )}

                    <div>
                      <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                        <Users className="w-4 h-4" />
                        Nutzer
                      </div>
                      <div className="text-white font-bold">
                        {offer.current_customers || 0} / {offer.max_customers}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                        <Calendar className="w-4 h-4" />
                        Gültig bis
                      </div>
                      <div className="text-white font-bold">
                        {new Date(offer.valid_until).toLocaleDateString('de-DE')}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {offer.coupon_code && (
                      <div className="inline-block px-4 py-2 rounded-lg" style={{ backgroundColor: '#7CD4FD20', border: '1px dashed #7CD4FD' }}>
                        <span className="text-xs text-gray-400 mr-2">CODE:</span>
                        <span className="font-mono font-bold" style={{ color: '#7CD4FD' }}>{offer.coupon_code}</span>
                      </div>
                    )}
                    
                    {offer.first_month_free && (
                      <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: '#22C55E20', color: '#22C55E' }}>
                        1. Monat gratis
                      </span>
                    )}
                    
                    {offer.requires_12_month && (
                      <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: '#F59E0B20', color: '#F59E0B' }}>
                        12-Monats-Abo
                      </span>
                    )}
                    
                    {offer.show_as_popup && (
                      <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: '#A78BFA20', color: '#A78BFA' }}>
                        Popup: {offer.popup_page_type === 'diagnose' ? 'Diagnose' : 'FMS'}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setEditingOffer(offer);
                      setShowCreateForm(true);
                    }}
                    variant="outline"
                    size="sm"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => handleDelete(offer.id)}
                    variant="outline"
                    size="sm"
                    className="text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {offers.length === 0 && (
          <Card style={{ backgroundColor: '#1A1F2E', borderColor: '#2A3142' }}>
            <CardContent className="p-12 text-center">
              <Sparkles className="w-16 h-16 mx-auto mb-4" style={{ color: '#7CD4FD' }} />
              <p className="text-gray-400 text-lg">Noch keine Aktionen erstellt</p>
              <Button onClick={handleCreate} className="mt-4 bg-gradient-to-r from-[#7CD4FD] to-[#22C55E]">
                Erste Aktion erstellen
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create/Edit Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}>
          <Card style={{ backgroundColor: '#1A1F2E', maxWidth: '600px', width: '100%', margin: 'auto', maxHeight: '90vh' }}>
            <div style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <CardHeader>
              <CardTitle style={{ color: '#7CD4FD' }}>
                {editingOffer?.id ? 'Angebot bearbeiten' : 'Neues Angebot erstellen'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm mb-2 block" style={{ color: '#94A3B8' }}>Name</label>
                <Input
                  value={editingOffer?.name || ''}
                  onChange={e => setEditingOffer({...editingOffer, name: e.target.value})}
                  placeholder="OBD Live Early-Bird"
                  style={{ backgroundColor: '#2A3142', color: '#E6EDF3', borderColor: '#3A4152' }}
                />
              </div>

              <div>
                <label className="text-sm mb-2 block" style={{ color: '#94A3B8' }}>Beschreibung</label>
                <Input
                  value={editingOffer?.description || ''}
                  onChange={e => setEditingOffer({...editingOffer, description: e.target.value})}
                  placeholder="Erstes Jahr 30% Rabatt"
                  style={{ backgroundColor: '#2A3142', color: '#E6EDF3', borderColor: '#3A4152' }}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm mb-2 block" style={{ color: '#94A3B8' }}>Rabatt-Typ</label>
                  <select
                    value={editingOffer?.discount_type || 'percentage'}
                    onChange={e => setEditingOffer({...editingOffer, discount_type: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg"
                    style={{ backgroundColor: '#2A3142', color: '#E6EDF3', border: '1px solid #3A4152' }}
                  >
                    <option value="percentage">Prozent (%)</option>
                    <option value="fixed">Fixer Betrag (€)</option>
                  </select>
                </div>

                <div>
                  <label className="text-sm mb-2 block" style={{ color: '#94A3B8' }}>Wert</label>
                  <Input
                    type="number"
                    value={editingOffer?.discount_value || ''}
                    onChange={e => setEditingOffer({...editingOffer, discount_value: parseFloat(e.target.value)})}
                    placeholder="30"
                    style={{ backgroundColor: '#2A3142', color: '#E6EDF3', borderColor: '#3A4152' }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm mb-2 block" style={{ color: '#94A3B8' }}>Gratis Item (optional)</label>
                  <Input
                    value={editingOffer?.free_item || ''}
                    onChange={e => setEditingOffer({...editingOffer, free_item: e.target.value})}
                    placeholder="obd_dongle"
                    style={{ backgroundColor: '#2A3142', color: '#E6EDF3', borderColor: '#3A4152' }}
                  />
                </div>

                <div>
                  <label className="text-sm mb-2 block" style={{ color: '#94A3B8' }}>Item Wert (€)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingOffer?.free_item_value || ''}
                    onChange={e => setEditingOffer({...editingOffer, free_item_value: parseFloat(e.target.value)})}
                    placeholder="49.90"
                    style={{ backgroundColor: '#2A3142', color: '#E6EDF3', borderColor: '#3A4152' }}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm mb-2 block" style={{ color: '#94A3B8' }}>Max. Kunden (Limit)</label>
                <Input
                  type="number"
                  value={editingOffer?.max_customers || ''}
                  onChange={e => setEditingOffer({...editingOffer, max_customers: parseInt(e.target.value)})}
                  placeholder="100"
                  style={{ backgroundColor: '#2A3142', color: '#E6EDF3', borderColor: '#3A4152' }}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm mb-2 block" style={{ color: '#94A3B8' }}>Gültig von</label>
                  <Input
                    type="date"
                    value={editingOffer?.valid_from?.split('T')[0] || ''}
                    onChange={e => setEditingOffer({...editingOffer, valid_from: e.target.value})}
                    style={{ backgroundColor: '#2A3142', color: '#E6EDF3', borderColor: '#3A4152' }}
                  />
                </div>

                <div>
                  <label className="text-sm mb-2 block" style={{ color: '#94A3B8' }}>Gültig bis</label>
                  <Input
                    type="date"
                    value={editingOffer?.valid_until?.split('T')[0] || ''}
                    onChange={e => setEditingOffer({...editingOffer, valid_until: e.target.value})}
                    style={{ backgroundColor: '#2A3142', color: '#E6EDF3', borderColor: '#3A4152' }}
                  />
                </div>
              </div>

              <div>
                <label className="text-sm mb-2 block" style={{ color: '#94A3B8' }}>Coupon Code (optional)</label>
                <Input
                  value={editingOffer?.coupon_code || ''}
                  onChange={e => setEditingOffer({...editingOffer, coupon_code: e.target.value.toUpperCase()})}
                  placeholder="EARLYBIRD2024"
                  style={{ backgroundColor: '#2A3142', color: '#E6EDF3', borderColor: '#3A4152' }}
                />
              </div>

              {/* Status */}
              <div className="border-t pt-4" style={{ borderColor: '#3A4152' }}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: '#7CD4FD' }}>Status</h3>
                
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingOffer?.is_active !== false}
                    onChange={e => setEditingOffer({...editingOffer, is_active: e.target.checked})}
                    className="w-4 h-4"
                    style={{ accentColor: '#22C55E' }}
                  />
                  <div>
                    <span className="text-sm font-semibold" style={{ color: '#E6EDF3' }}>Aktion aktiv</span>
                    <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                      Nur aktive Aktionen werden angezeigt und können verwendet werden
                    </p>
                  </div>
                </label>
              </div>

              {/* Special Offer Options */}
              <div className="border-t pt-4" style={{ borderColor: '#3A4152' }}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: '#7CD4FD' }}>Spezielle Angebote</h3>
                
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingOffer?.first_month_free || false}
                      onChange={e => setEditingOffer({...editingOffer, first_month_free: e.target.checked})}
                      className="w-4 h-4"
                      style={{ accentColor: '#1EA0E6' }}
                    />
                    <span className="text-sm" style={{ color: '#E6EDF3' }}>Erster Monat gratis</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingOffer?.requires_12_month || false}
                      onChange={e => setEditingOffer({...editingOffer, requires_12_month: e.target.checked})}
                      className="w-4 h-4"
                      style={{ accentColor: '#1EA0E6' }}
                    />
                    <span className="text-sm" style={{ color: '#E6EDF3' }}>Nur für 12-Monats-Abo</span>
                  </label>
                </div>
              </div>

              {/* Popup Settings */}
              <div className="border-t pt-4" style={{ borderColor: '#3A4152' }}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: '#7CD4FD' }}>Landing Page Popup</h3>
                
                <label className="flex items-center gap-3 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    checked={editingOffer?.show_as_popup || false}
                    onChange={e => setEditingOffer({...editingOffer, show_as_popup: e.target.checked})}
                    className="w-4 h-4"
                    style={{ accentColor: '#1EA0E6' }}
                  />
                  <span className="text-sm" style={{ color: '#E6EDF3' }}>Als Popup auf Landing Page anzeigen</span>
                </label>

                {editingOffer?.show_as_popup && (
                  <div className="grid grid-cols-2 gap-4 pl-7">
                    <div>
                      <label className="text-sm mb-2 block" style={{ color: '#94A3B8' }}>Seiten-Typ</label>
                      <select
                        value={editingOffer?.popup_page_type || 'diagnose'}
                        onChange={e => setEditingOffer({...editingOffer, popup_page_type: e.target.value})}
                        className="w-full px-3 py-2 rounded-lg"
                        style={{ backgroundColor: '#2A3142', color: '#E6EDF3', border: '1px solid #3A4152' }}
                      >
                        <option value="diagnose">Diagnose Landing Page</option>
                        <option value="fms">FMS Landing Page</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-sm mb-2 block" style={{ color: '#94A3B8' }}>Verzögerung (Sek.)</label>
                      <Input
                        type="number"
                        value={editingOffer?.popup_delay || 10}
                        onChange={e => setEditingOffer({...editingOffer, popup_delay: parseInt(e.target.value)})}
                        placeholder="10"
                        style={{ backgroundColor: '#2A3142', color: '#E6EDF3', borderColor: '#3A4152' }}
                      />
                    </div>
                  </div>
                )}

                {editingOffer?.show_as_popup && (
                  <p className="text-xs mt-2 pl-7" style={{ color: '#94A3B8' }}>
                    ⚠️ Pro Seiten-Typ kann nur ein Popup aktiv sein
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button onClick={handleSave} className="flex-1 bg-gradient-to-r from-[#7CD4FD] to-[#22C55E]">
                  Speichern
                </Button>
                <Button onClick={() => {
                  setShowCreateForm(false);
                  setEditingOffer(null);
                }} variant="outline">
                  Abbrechen
                </Button>
              </div>
            </CardContent>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
